import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Webhook, CheckCircle, AlertCircle, RefreshCw, Settings } from 'lucide-react';

type Environment = 'staging' | 'production';

// Detectar ambiente automaticamente baseado na URL da API
const detectEnvironment = (): Environment => {
  const apiUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '';
  return apiUrl.includes('staging') ? 'staging' : 'production';
};

export const WebhookConfig: React.FC = () => {
  // Ambiente detectado automaticamente (não pode ser alterado pelo usuário)
  const environment = detectEnvironment();
  const [isLoading, setIsLoading] = useState(false);
  const [webhookStatus, setWebhookStatus] = useState<any>(null);
  const [autoSetupUrl, setAutoSetupUrl] = useState('');
  const [manualWebhookUrl, setManualWebhookUrl] = useState('');
  const [manualVerifyToken, setManualVerifyToken] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Environment-specific URLs
  const environments = {
    staging: {
      baseUrl: 'https://itororo-mensageria-staging.libellus.com.br',
      webhookUrl: 'https://itororo-mensageria-staging.libellus.com.br/api/webhook',
      verifyToken: 'staging-webhook-token-change-this',
    },
    production: {
      baseUrl: 'https://itororosac.libellus.com.br',
      webhookUrl: 'https://itororosac.libellus.com.br/api/webhook',
      verifyToken: 'production-webhook-token-change-this',
    },
  };

  useEffect(() => {
    loadWebhookStatus();
    // Set default URLs based on detected environment
    const env = environments[environment];
    setAutoSetupUrl(env.baseUrl);
    setManualWebhookUrl(env.webhookUrl);
    setManualVerifyToken(env.verifyToken);
  }, []);

  const loadWebhookStatus = async () => {
    try {
      const status = await api.getWebhookStatus();
      setWebhookStatus(status);
    } catch (error: any) {
      console.error('Failed to load webhook status:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Erro ao carregar status do webhook' });
    }
  };

  const handleAutoSetup = async () => {
    if (!autoSetupUrl.trim()) {
      setMessage({ type: 'error', text: 'Por favor, informe a URL base' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const result = await api.autoSetupWebhook(autoSetupUrl);
      setMessage({ type: 'success', text: result.message || 'Webhook configurado com sucesso!' });
      await loadWebhookStatus();
    } catch (error: any) {
      console.error('Failed to setup webhook:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Erro ao configurar webhook' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualSetup = async () => {
    if (!manualWebhookUrl.trim() || !manualVerifyToken.trim()) {
      setMessage({ type: 'error', text: 'Por favor, preencha todos os campos' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const result = await api.setupWebhook(manualWebhookUrl, manualVerifyToken);
      setMessage({ type: 'success', text: result.message || 'Webhook configurado com sucesso!' });
      await loadWebhookStatus();
    } catch (error: any) {
      console.error('Failed to setup webhook:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Erro ao configurar webhook' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configuração de Webhook</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Configure o webhook para receber mensagens do WhatsApp
        </p>
      </div>

      {/* Environment Indicator */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ambiente Detectado
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Baseado na configuração do build
            </p>
          </div>
          <div className={`px-6 py-3 rounded-lg font-medium ${
            environment === 'staging'
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
              : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
          }`}>
            {environment === 'staging' ? 'Staging' : 'Production'}
          </div>
        </div>
      </div>

      {/* Webhook Status */}
      {webhookStatus && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Status Atual</h2>
            <button
              onClick={loadWebhookStatus}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
              title="Atualizar status"
            >
              <RefreshCw className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <span className="text-sm text-gray-700 dark:text-gray-300">Webhook Ativo</span>
              {webhookStatus.active ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-500" />
              )}
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <span className="text-sm text-gray-700 dark:text-gray-300">WhatsApp Configurado</span>
              {webhookStatus.whatsappConfigured ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-500" />
              )}
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <span className="text-sm text-gray-700 dark:text-gray-300">Token de Verificação</span>
              {webhookStatus.webhookVerifyTokenSet ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-500" />
              )}
            </div>
            {webhookStatus.webhookUrl && (
              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <span className="text-sm text-gray-700 dark:text-gray-300 block mb-1">URL do Webhook</span>
                <code className="text-xs text-blue-600 dark:text-blue-400 break-all">
                  {webhookStatus.webhookUrl}
                </code>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Message Display */}
      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200'
              : 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200'
          }`}
        >
          <div className="flex items-center space-x-2">
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <p className="text-sm font-medium">{message.text}</p>
          </div>
        </div>
      )}

      {/* Auto Setup */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Settings className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Configuração Automática (Recomendado)
          </h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Configure automaticamente usando a URL base do ambiente <strong className="text-gray-900 dark:text-white">{environment}</strong> (detectado automaticamente)
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              URL Base
            </label>
            <input
              type="url"
              value={autoSetupUrl}
              onChange={(e) => setAutoSetupUrl(e.target.value)}
              placeholder={environments[environment].baseUrl}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              O endpoint /api/webhook será adicionado automaticamente
            </p>
          </div>

          <button
            onClick={handleAutoSetup}
            disabled={isLoading || !autoSetupUrl.trim()}
            className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span>Configurando...</span>
              </>
            ) : (
              <>
                <Webhook className="w-5 h-5" />
                <span>Configurar Automaticamente</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Manual Setup */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Settings className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Configuração Manual</h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Configure manualmente especificando URL completa e token de verificação
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              URL do Webhook
            </label>
            <input
              type="url"
              value={manualWebhookUrl}
              onChange={(e) => setManualWebhookUrl(e.target.value)}
              placeholder={environments[environment].webhookUrl}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Token de Verificação
            </label>
            <input
              type="text"
              value={manualVerifyToken}
              onChange={(e) => setManualVerifyToken(e.target.value)}
              placeholder={environments[environment].verifyToken}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Deve corresponder ao WEBHOOK_VERIFY_TOKEN do arquivo .env
            </p>
          </div>

          <button
            onClick={handleManualSetup}
            disabled={isLoading || !manualWebhookUrl.trim() || !manualVerifyToken.trim()}
            className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span>Configurando...</span>
              </>
            ) : (
              <>
                <Webhook className="w-5 h-5" />
                <span>Configurar Manualmente</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-3">
          ℹ️ Instruções Importantes
        </h3>
        <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
          <li>• O ambiente é detectado automaticamente baseado na configuração do build</li>
          <li>• O webhook deve estar acessível publicamente pela internet</li>
          <li>• Após configurar, envie uma mensagem de teste para verificar o funcionamento</li>
          <li>• Em caso de problemas, verifique os logs do servidor e as variáveis de ambiente</li>
          <li>• Token atual: <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">{environments[environment].verifyToken}</code></li>
        </ul>
      </div>
    </div>
  );
};
